import { NavLink, useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Search,
  Package,
  Users,
  Moon,
  Sun,
  LogOut,
  Home,
  Link,
  Instagram,
  MonitorSpeaker,
  Percent,
  FileText,
  MapPin,
  Building2,
  ChevronRight,
  UserCog,
  Loader2,
  Sparkles,
  Stethoscope,
  BarChart3,
  UsersRound,
  CalendarDays,
  CalendarRange,
  Filter,
  ReceiptText,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";
import AccountSelector from "@/components/accounts/AccountSelector";
import { useAccountIntegrationsData } from "@/hooks/useAccountIntegrationsData";
import { ehAssinante } from "@/lib/account-access-context";
import { cn } from "@/lib/utils";
import type { ProfileResponse } from "@/services/user.service";

interface AppSidebarProps {
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  user: ProfileResponse | null;
  onOpenAccountSelector?: () => void;
}

const menuItems = [
  {
    title: "Dashboard",
    items: [
      { id: "dashboard", title: "Visão Geral", icon: Home, path: "/dashboard" },
      // As duas análises por canal são o detalhamento do CONTROLE DIÁRIO da Visão Geral, e
      // até o item 25 da auditoria só se chegava a elas pelo botão daquele card — o que fez
      // a auditoria registrar a mensal como "página ausente". Elas continuam encadeadas uma
      // na outra; o menu passa a ser o segundo caminho.
      {
        id: "analise-diaria",
        title: "Análise Diária",
        icon: CalendarDays,
        path: "/dashboard/analise-diaria",
      },
      {
        id: "analise-mensal",
        title: "Análise Mensal",
        icon: CalendarRange,
        path: "/dashboard/analise-mensal",
      },
      // <auditoria 31> O DETALHAMENTO ETAPA 1 fica ao lado das duas análises por canal, e
      // pelo mesmo motivo: ele é o detalhamento de um card da Visão Geral — a etapa 1 do
      // FUNIL DE VENDAS —, e o item 25 já mostrou que tela sem entrada de menu é lida como
      // página que não existe.
      {
        id: "detalhamento-etapa-1",
        title: "Detalhamento Etapa 1",
        icon: Filter,
        path: "/dashboard/detalhamento-etapa-1",
      },
      {
        id: "benchmark",
        title: "Benchmark",
        icon: BarChart3,
        path: "/dashboard/benchmark",
      },
      // { id: "ai-insights", title: "Insights IA", icon: Bot, path: "/dashboard/ai-insights" },
    ],
  },
  {
    title: "Performance de Mídia",
    items: [
      {
        id: "products",
        title: "Produtos",
        icon: Package,
        path: "/dashboard/products",
      },
      {
        id: "conversion-rate",
        title: "Taxa de Conversão",
        icon: Percent,
        path: "/dashboard/conversion-rate",
      },
      {
        id: "acquisition",
        title: "Aquisição",
        icon: Users,
        path: "/dashboard/acquisition",
      },
      // <auditoria 28> A ANÁLISE DE PEDIDOS entra logo abaixo de Aquisição, que é de onde
      // ela vem: a auditoria a registrou como "página ausente", e o item 25 já tinha
      // mostrado que uma tela sem entrada de menu é lida como página que não existe.
      {
        id: "analise-pedidos",
        title: "Análise de Pedidos",
        icon: ReceiptText,
        path: "/dashboard/analise-pedidos",
      },
      { id: "ads", title: "ADS", icon: TrendingUp, path: "/dashboard/ads" },
      { id: "utm", title: "UTM", icon: Link, path: "/dashboard/utm" },
      {
        id: "pages",
        title: "Páginas",
        icon: FileText,
        path: "/dashboard/pages",
      },
      {
        id: "receita",
        title: "Receita",
        icon: TrendingUp,
        path: "/dashboard/receita",
      },
      {
        id: "regiao",
        title: "Região",
        icon: MapPin,
        path: "/dashboard/regiao",
      },
      { id: "seo", title: "SEO", icon: Search, path: "/dashboard/seo" },
      {
        id: "instagram",
        title: "Instagram",
        icon: Instagram,
        path: "/dashboard/instagram",
      },
      {
        id: "historico",
        title: "Histórico",
        icon: FileText,
        path: "/dashboard/historico",
      },
      {
        id: "media-performance",
        title: "Performance Geral",
        icon: MonitorSpeaker,
        path: "/dashboard/media-performance",
      },
      {
        id: "media-chat",
        title: "Arquiteto",
        icon: Sparkles,
        path: "/dashboard/media-chat",
      },
      {
        id: "diagnostico",
        title: "Diagnóstico",
        icon: Stethoscope,
        path: "/dashboard/diagnostico",
      },
    ],
  },
  {
    title: "Configurações",
    items: [
      {
        id: "my-account",
        title: "Minha Conta",
        icon: UserCog,
        path: "/dashboard/my-account",
      },
      {
        id: "accounts",
        title: "Contas da Agência",
        icon: Building2,
        path: "/dashboard/accounts",
      },
      {
        id: "teams",
        title: "Times",
        icon: UsersRound,
        path: "/dashboard/teams",
      },
      {
        id: "integrations",
        title: "Integrações",
        icon: Link,
        path: "/dashboard/integrations",
      },
    ],
  },
];

function AppSidebar({
  onLogout,
  darkMode,
  onToggleDarkMode,
  user,
  onOpenAccountSelector,
}: AppSidebarProps) {
  const navigate = useNavigate();
  const { state, isMobile } = useSidebar();
  const { isAgencyUser, canSwitchAccounts, isTeamScoped, isReadOnly } =
    useAccount();
  const { canManageTeams, isTeamMember } = useAuth();
  const { syncingBySource } = useAccountIntegrationsData({
    refetchInterval: 60000,
  });
  const isSyncing = syncingBySource.google || syncingBySource.meta;

  return (
    <Sidebar className="border-r border-border/40 bg-card">
      <SidebarHeader className="border-b border-border/40 p-4">
        <div className="flex items-center justify-between">
          <img
            src="/logoSidebar.png"
            alt="Painel do E-Commerce"
            className={cn(
              "transition-all duration-200",
              state === "collapsed" ? "w-8" : "w-32",
            )}
          />
          {!isMobile && <SidebarTrigger className="h-8 w-8" />}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-3">
        {/* Seletor de Contas - para quem tem mais de uma conta */}
        {canSwitchAccounts && state === "expanded" && (
          <div className="mb-4 px-2">
            <div className="p-3 bg-muted/40 rounded-lg border border-border/40">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-1.5 w-1.5 bg-primary rounded-full" />
                <span className="text-xs font-medium text-foreground">
                  Conta Ativa
                </span>
              </div>
              <AccountSelector onOpen={onOpenAccountSelector} />
            </div>
          </div>
        )}

        {/* Status de sincronização */}
        {state === "expanded" && isSyncing && (
          <div className="mb-4 px-2">
            <button
              onClick={() => navigate("/dashboard/integrations")}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-blue-600 bg-blue-500/10 hover:bg-blue-500/20 transition-colors border border-blue-500/20"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Sincronizando dados...
            </button>
          </div>
        )}

        {menuItems.map((group) => (
          <SidebarGroup key={group.title} className="mb-1">
            <SidebarGroupLabel className="text-2xs font-medium text-muted-foreground uppercase tracking-wider px-3 py-2">
              {group.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items
                  .map((item) => {
                    // <M-279> "Contas da Agência" é de todo dono de conta — o assinante —, e não
                    // só de quem já tem mais de uma: é nela que se cadastra a segunda loja, e
                    // escondê-la de quem tem uma só trancava o lojista no plano que permite
                    // cinco. Quem não é assinante (o usuário convidado para uma loja) leva
                    // `403` de `GET /api/v1/accounts`, e não vê o item.
                    //
                    // <M-248> Duas perguntas, e não uma. "Contas da Agência" é a agência
                    // gerindo as contas dela, e `GET /api/v1/accounts` responde
                    // `403 Usuário não é um assinante` a quem navega por escopo cedido —
                    // qualquer que seja o nível dele. Já conectar integração é escrita **na
                    // loja aberta**, e quem for `editor` nela pode: esconder o item de um
                    // editor era tirar dele o que quem administra a conta lhe deu.
                    if (
                      item.id === "accounts" &&
                      (isTeamScoped || !ehAssinante(user))
                    ) {
                      return null;
                    }

                    if (isReadOnly && item.id === "integrations") {
                      return null;
                    }

                    // Times: quem administra a agência gere; quem é membro consulta os seus.
                    // Para os demais o item não descreve tela nenhuma que eles possam abrir.
                    if (
                      item.id === "teams" &&
                      !canManageTeams &&
                      !isTeamMember
                    ) {
                      return null;
                    }

                    // "Contas da Agência" só descreve a tela de quem é agência, e "Times"
                    // vira "Meus times" para quem não os administra.
                    const title =
                      item.id === "accounts" && !isAgencyUser
                        ? "Minhas Contas"
                        : item.id === "teams" && !canManageTeams
                          ? "Meus times"
                          : item.title;

                    return (
                      <SidebarMenuItem key={item.id}>
                        <NavLink
                          to={item.path}
                          end={item.path === "/dashboard"}
                        >
                          {({ isActive }) => (
                            <SidebarMenuButton
                              isActive={isActive}
                              className={cn(
                                "w-full justify-start gap-3 px-3 py-2 rounded-lg transition-all duration-150",
                                "hover:bg-muted/60",
                                isActive &&
                                  "bg-primary/10 text-primary font-medium",
                              )}
                              tooltip={
                                state === "collapsed" ? title : undefined
                              }
                            >
                              <item.icon
                                className={cn(
                                  "h-4 w-4 transition-colors",
                                  isActive
                                    ? "text-primary"
                                    : "text-muted-foreground",
                                )}
                              />
                              <span className="text-sm">{title}</span>
                              {isActive && state === "expanded" && (
                                <ChevronRight className="h-3 w-3 ml-auto text-primary/60" />
                              )}
                            </SidebarMenuButton>
                          )}
                        </NavLink>
                      </SidebarMenuItem>
                    );
                  })
                  .filter(Boolean)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-border/40 p-3">
        <div className="space-y-3">
          {/*
            <M-249> O bloco do usuário é o caminho para "Meu Perfil". Ele existia só como
            enfeite, e a página de perfil, senha, 2FA e sessões estava montada em
            `/dashboard/account` sem **um** link que apontasse para ela em todo o
            `frontend` — ninguém tinha como trocar a própria senha pelo painel. Ela não
            entra na lista de menu de propósito: "Minha Conta", logo acima, é outra tela
            (usuários da loja, faturas e assinatura), e dois itens de nome parecido lado a
            lado eram metade da confusão que criou este item.
          */}
          <NavLink
            to="/dashboard/account"
            title="Meu Perfil"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 p-2 rounded-lg transition-colors",
                state === "collapsed" && "justify-center",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "bg-muted/30 hover:bg-muted/60",
              )
            }
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            {state === "expanded" && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-2xs text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            )}
          </NavLink>

          <div
            className={cn("flex gap-1.5", state === "collapsed" && "flex-col")}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleDarkMode}
              className={cn(
                "h-8 text-xs",
                state === "collapsed" ? "w-full justify-center" : "flex-1",
              )}
              title={darkMode ? "Modo claro" : "Modo escuro"}
            >
              {darkMode ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
              {state === "expanded" && (
                <span className="ml-1.5">{darkMode ? "Claro" : "Escuro"}</span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className={cn(
                "h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10",
                state === "collapsed" ? "w-full justify-center" : "flex-1",
              )}
              title="Sair"
            >
              <LogOut className="h-4 w-4" />
              {state === "expanded" && <span className="ml-1.5">Sair</span>}
            </Button>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
